/**
 * Shared client-side types for the Phase 3 Workflow admin page.
 * These mirror the server-computed contracts from the Phase 2 rules engine
 * (GET /api/admin/workflow/v2/*, /applicants/:uid/full, /bulk/advance).
 * The UI never computes workflow state — it only renders this contract.
 */
import type { CanonicalStage } from '../../../lib/workflow/transitions';
import type { StagePrerequisite, AiEligibilityVerdict } from '../../../lib/workflow/types';

export interface StructuredBlocker {
  message: string;
  owner: string;
  gateKey?: string;
}

export interface WorkflowRecord {
  uid: string;
  displayName: string;
  email: string;
  applicationId: string;
  program: string;
  season: string;
  stage: CanonicalStage | string;
  subStatus: string;
  readyToAdvance: boolean;
  nextTransitionMode: 'auto' | 'manual';
  overridden: boolean;
  closureType: 'SD' | 'SC' | 'SP' | null;
  blockers: string[];
  blockerCount: number;
  topBlocker: { message: string; owner: string } | null;
  ownerName: string;
  /** Assignee uid, citizenship country, and referrer org id — carried
   *  by the v2/records payload for the unified filter bar. */
  ownerUid?: string | null;
  country?: string | null;
  referrerId?: string | null;
  daysInStatus: number;
  isStale: boolean;
  isPastTarget: boolean;
  confirmationType: 'placement' | 'admission' | 'none';
  confirmationLabel: string;
  lastActivityAt: string | null;
  balanceDue: number;
  currency: string;
  /** Read-time synthesized pre-claim entry (referrer-added candidate with no user account yet). */
  unclaimed?: boolean;
}

export interface StageSummary {
  key: CanonicalStage | string;
  label: string;
  count: number;
  ready: number;
  blocked: number;
  stale: number;
  paused: boolean;
  topSubStatuses: { label: string; count: number }[];
  subStatusCount: number;
}

export interface WorkflowSummary {
  generatedAt: string;
  totals: {
    active: number;
    ready: number;
    blocked: number;
    needsAttention: number;
    overridden: number;
  };
  stages: StageSummary[];
  closure: { SD: number; SC: number; SP: number };
  queues: {
    awaitingDecision: number;
    financeReview: number;
    redlistReview: number;
    disclosureReview: number;
    pastDeadline: number;
    stale: number;
    overridden: number;
    paused: number;
  };
  unmapped: number;
  automation: {
    autoAdvanceEnabled: boolean;
    autoAdvancePaused: boolean;
    emailsPaused: boolean;
    emailsPausedBy: string | null;
    emailsPausedAt: string | null;
    nextRunAt: string | null;
    lastRun: { at: string; processed: number; advanced: number; skipped: number; runId: string } | null;
  };
}

export interface PreApprovalCheck {
  code: string;
  label: string;
  state: 'pass' | 'pending' | 'review' | 'not_applicable';
  detail?: string;
  owner?: string;
}

/**
 * One unmet, non-overridden gate from the server's 409 UNMET_GATES payload
 * (advance / pre-approval approve). The approve-with-override flow lists
 * these and requires an individual reason per selected gate.
 */
export interface UnmetGate {
  key: string;
  label: string;
  detail?: string | null;
  aiVerdict?: AiEligibilityVerdict | null;
}

export interface FullApplicantRecord {
  uid: string;
  displayName: string;
  email: string;
  program: string;
  season: string;
  stage: CanonicalStage | string;
  subStatus: string;
  blockers: string[];
  structuredBlockers: StructuredBlocker[];
  readyToAdvance: boolean;
  readyToAdvanceAt: string | null;
  nextTransitionMode: 'auto' | 'manual';
  overridden: boolean;
  closureType: 'SD' | 'SC' | 'SP' | null;
  preApprovalChecks: PreApprovalCheck[];
  /** Stage-incremental prerequisites for the current stage only (server-computed). */
  stagePrerequisites?: StagePrerequisite[];
  preApprovalDecision: string | null;
  ball: string;
  reason: string;
  flags: string[];
  applicationId: string;
  ownerName: string;
  lastActivityAt: string | null;
  /** True for the read-only pre-claim drawer payload (no workflow record exists yet). */
  unclaimed?: boolean;
}

export interface ApplicantFull {
  record: FullApplicantRecord;
  history: { id: string; from: string; to: string; at: string; trigger: string; note?: string }[];
  notes: { id: string; body: string; byName: string; at: string }[];
  application: {
    applicationId: string;
    applicationDeadline: string | null;
    confirmationType: 'placement' | 'admission' | 'none';
    placements: any[];
    emergencyContacts: any[];
    personalDetails: Record<string, any>;
    preApprovalDecision: string | null;
    [k: string]: any;
  };
  screenings: any[];
  invoices: { id: string; status: string; total: number; paidAmount: number; [k: string]: any }[];
  emailLog: { id: string; templateKey: string; trigger: string; status: string; sentAt: string }[];
  /** Documents merged from the applications/{id}/documents subcollection + legacy embedded array. */
  documents: any[];
}

export interface BulkPreviewItem {
  uid: string;
  displayName: string;
  applicationId: string;
  stage: string;
  category: 'eligible' | 'blocked' | 'manual' | 'already_processed' | 'not_found';
  reason?: string;
  from?: string;
  to?: string;
}

export interface BulkPreview {
  preview: true;
  items: BulkPreviewItem[];
  summary: { eligible: number; blocked: number; manual: number; alreadyProcessed: number };
  total: number;
}

export interface BulkResultItem extends BulkPreviewItem {
  ok: boolean;
  advanced: boolean;
}

export interface BulkConfirmResult {
  preview: false;
  runId: string;
  results: BulkResultItem[];
  summary: { advanced: number; skipped: number };
  total: number;
}

export type QueueKey =
  | 'ready'
  | 'blocked'
  | 'needsAttention'
  | 'overridden'
  | 'awaitingDecision'
  | 'financeReview'
  | 'redlistReview'
  | 'disclosureReview'
  | 'pastDeadline'
  | 'stale'
  | 'paused';

export const QUEUE_LABELS: Record<QueueKey, string> = {
  ready: 'Ready to Matriculate',
  blocked: 'Blocked',
  needsAttention: 'Needs attention',
  overridden: 'Overridden',
  awaitingDecision: 'Awaiting your decision',
  financeReview: 'Finance Review',
  redlistReview: 'Redlist Review',
  disclosureReview: 'Disclosure Review',
  pastDeadline: 'Past Deadline',
  stale: 'Stale',
  paused: 'Paused',
};
